/**
 * [INPUT]: 依赖共享 API 的游戏/Prompt 契约、Authorization token 协调器与统一 API 基地址。
 * [OUTPUT]: 对外提供 AdminApi 真实 HTTP 实现，覆盖 Prompt CRUD、游戏/素材、公告、LLM、计费和 S3。
 * [POS]: admin-web infrastructure 适配层；归一化 Prompt snake_case 与输入/输出限额，所有私有 API 禁止缓存。
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { ApiClient, ApiClientError, parseAuthTokens, type ApiError, type GameSummary as SharedGameSummary } from '@elseyou/api-client';
import type {
  AdminUser,
  AIRun,
  Announcement,
  AnnouncementListOptions,
  AnnouncementPlatform,
  AnnouncementStatus,
  AnnouncementWriteInput,
  ApiListResult,
  AuditLog,
  DashboardMetrics,
  ConfirmAssetUploadInput,
  GameAsset,
  GameAIPromptConfig,
  GameAIPromptWriteInput,
  GameAIPrompt,
  GameAIPromptEntryWriteInput,
  GameCoverSlot,
  GameCoverVariants,
  GameRelease,
  GameStatus,
  GameSummary,
  GameUpdateInput,
  GameWriteInput,
  InspirationLedgerEntry,
  RedeemCode,
  RedeemCodeWriteInput,
  LLMChannel,
  LLMChannelWriteInput,
  AIPriceRule,
  AIPriceRuleWriteInput,
  InspirationPackageReference,
  S3ConnectionResult,
  S3Settings,
  S3SettingsInput,
  PresignAssetUpload,
  PresignAssetUploadInput,
} from '../types';
import type { AdminApi } from './admin-api';
import { apiBaseUrl } from './api-config';
import { adminAuthTokens, clearAdminAuthTokens, getAdminAuthTokens, saveAdminAuthTokens } from './auth-tokens';
import { createClientId } from './utils';

const sharedClient = new ApiClient({
  baseUrl: apiBaseUrl,
  credentials: 'omit',
  accessToken: () => getAdminAuthTokens()?.accessToken,
});
let refreshPromise: Promise<boolean> | null = null;

function requestHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  headers.set('accept', 'application/json');
  headers.set('X-Elseyou-Client', 'admin');
  const accessToken = getAdminAuthTokens()?.accessToken;
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  return headers;
}

async function request<T>(path: string, init?: RequestInit, retryAuth = true): Promise<T> {
  const current = getAdminAuthTokens();
  if (!path.startsWith('/auth/') && current && adminAuthTokens.isAccessTokenExpiring(current)) {
    await refreshSession(current.accessToken);
  }
  let response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    cache: 'no-store',
    credentials: 'omit',
    headers: requestHeaders(init?.headers),
  });
  if (response.status === 401 && retryAuth && !path.startsWith('/auth/')) {
    const refreshed = await refreshSession(current?.accessToken ?? '');
    if (refreshed) return request<T>(path, init, false);
  }
  if (!response.ok) {
    let payload: ApiError | undefined;
    try { payload = await response.json() as typeof payload; } catch { /* 非 JSON 错误保留状态码。 */ }
    throw new ApiClientError(payload?.message ?? `Request failed: ${response.status}`, response.status, payload);
  }
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

async function refreshSession(failedAccessToken = ''): Promise<boolean> {
  const current = getAdminAuthTokens();
  if (failedAccessToken && current?.accessToken !== failedAccessToken) return Boolean(current?.accessToken);
  if (refreshPromise) return refreshPromise;

  refreshPromise = adminAuthTokens.withRefreshLock(async () => {
    const latest = getAdminAuthTokens();
    if (failedAccessToken && latest?.accessToken !== failedAccessToken) return Boolean(latest?.accessToken);
    if (!latest?.refreshToken) return false;
    const usedRefreshToken = latest.refreshToken;
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}/auth/token/refresh`, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'omit',
        headers: { accept: 'application/json', 'content-type': 'application/json', 'X-Elseyou-Client': 'admin' },
        body: JSON.stringify({ refresh_token: usedRefreshToken }),
      });
    } catch {
      return false;
    }
    if (!response.ok) {
      if (response.status === 401) clearAdminAuthTokens(usedRefreshToken);
      return false;
    }
    const nextTokens = parseAuthTokens(await response.json().catch(() => null));
    if (!nextTokens) {
      clearAdminAuthTokens(usedRefreshToken);
      return false;
    }
    if (getAdminAuthTokens()?.refreshToken !== usedRefreshToken) return Boolean(getAdminAuthTokens()?.accessToken);
    saveAdminAuthTokens(nextTokens);
    return true;
  }).catch(() => false).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value ? value : fallback;
}

function number(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function mapAnnouncement(raw: unknown): Announcement {
  const source = record(raw);
  const platform = text(source.platform, 'all');
  const status = text(source.status, 'draft');
  const platforms: AnnouncementPlatform[] = ['all', 'web', 'app', 'wx-miniapp'];
  const statuses: AnnouncementStatus[] = ['draft', 'scheduled', 'published', 'archived'];
  return {
    id: text(source.id ?? source.ID),
    title: text(source.title ?? source.Title),
    summary: text(source.summary ?? source.Summary),
    bodyMarkdown: text(source.body_markdown ?? source.bodyMarkdown ?? source.BodyMarkdown),
    platform: platforms.includes(platform as AnnouncementPlatform) ? platform as AnnouncementPlatform : 'all',
    isPinned: Boolean(source.is_pinned ?? source.isPinned ?? source.IsPinned),
    status: statuses.includes(status as AnnouncementStatus) ? status as AnnouncementStatus : 'draft',
    publishAt: typeof (source.publish_at ?? source.publishAt ?? source.PublishAt) === 'string' ? String(source.publish_at ?? source.publishAt ?? source.PublishAt) : null,
    archiveAt: typeof (source.archive_at ?? source.archiveAt ?? source.ArchiveAt) === 'string' ? String(source.archive_at ?? source.archiveAt ?? source.ArchiveAt) : null,
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
    updatedAt: text(source.updated_at ?? source.updatedAt ?? source.UpdatedAt, new Date(0).toISOString()),
  };
}

function announcementPayload(input: AnnouncementWriteInput): Record<string, unknown> {
  return {
    title: input.title.trim(),
    summary: input.summary?.trim() ?? '',
    body_markdown: input.bodyMarkdown.trim(),
    platform: input.platform,
    is_pinned: Boolean(input.isPinned),
    publish_at: input.publishAt || null,
    archive_at: input.archiveAt || null,
  };
}

function mapCoverVariants(raw: unknown): GameCoverVariants {
  const source = record(raw);
  const result: GameCoverVariants = {};
  for (const slot of ['hero', 'card', 'thumb'] as const) {
    const value = record(source[slot]);
    const ratio = text(value.ratio);
    const url = text(value.url);
    const assetId = text(value.asset_id ?? value.assetId);
    if (!url || !assetId || !(['4:3', '4:5', '1:1'] as string[]).includes(ratio)) continue;
    result[slot] = { assetId, url, ratio: ratio as '4:3' | '4:5' | '1:1' };
  }
  return result;
}

function mapSharedGame(game: SharedGameSummary): GameSummary {
  const status: GameStatus = game.status === 'published' ? 'published' : game.status === 'unpublished' ? 'paused' : 'draft';
  return {
    id: game.id,
    slug: game.slug,
    title: game.title,
    category: game.category,
    tags: game.tags ?? [],
    description: game.description,
    status,
    version: game.version || 'unversioned',
    runtimeVersion: game.runtimeMinVersion || '未声明',
    saveSchemaVersion: game.saveSchemaVersion,
    players: null,
    completionRate: null,
    lastPublishedAt: game.publishedAt ?? null,
    updatedAt: game.updatedAt,
    cover: game.coverUrl ?? '',
    coverVariants: mapCoverVariants(game.coverVariants),
    capabilities: game.capabilities,
  };
}

function mapRelease(raw: unknown): GameRelease {
  const source = record(raw);
  return {
    id: text(source.id ?? source.ID),
    gameId: text(source.game_id ?? source.gameId ?? source.GameID),
    version: text(source.version ?? source.Version, 'unversioned'),
    siteBuildId: text(source.site_build_id ?? source.siteBuildId ?? source.SiteBuildID),
    status: text(source.status ?? source.Status) === 'published' ? 'published' : text(source.status ?? source.Status) === 'unpublished' ? 'rolled_back' : 'staged',
    changelog: text(record(source.manifest).changelog, '未填写变更摘要'),
    checksum: text(record(source.manifest).checksum),
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
    createdBy: text(record(source.manifest).created_by, 'system'),
  };
}

function mapUser(raw: unknown): AdminUser {
  const source = record(raw);
  return {
    id: text(source.id ?? source.ID),
    phone: text(source.phone ?? source.Phone),
    nickname: text(source.display_name ?? source.displayName ?? source.DisplayName, '旅行者'),
    role: text(source.role ?? source.Role, 'user'),
    status: 'active',
    inspiration: source.inspiration === undefined ? null : number(source.inspiration),
    gamesPlayed: source.games_played === undefined ? null : number(source.games_played),
    lastSeenAt: typeof (source.updated_at ?? source.updatedAt ?? source.UpdatedAt) === 'string' ? String(source.updated_at ?? source.updatedAt ?? source.UpdatedAt) : null,
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapLedger(raw: unknown): InspirationLedgerEntry {
  const source = record(raw);
  const kind = text(source.kind ?? source.type ?? source.Kind, 'consume');
  const normalizedKind: InspirationLedgerEntry['kind'] = ['grant', 'consume', 'release', 'refund', 'adjustment', 'purchase', 'checkin', 'invite', 'redeem', 'expire'].includes(kind) ? kind as InspirationLedgerEntry['kind'] : 'consume';
  return {
    id: text(source.id ?? source.ID) || createClientId(),
    userId: text(source.user_id ?? source.userId ?? source.UserID),
    userNickname: text(source.user_nickname, '用户'),
    kind: normalizedKind,
    amount: number(source.amount ?? source.Amount),
    balanceAfter: source.balance_after === undefined ? null : number(source.balance_after),
    reason: text(source.reason ?? source.reference ?? source.Reference, '积分变动'),
    reference: text(source.reference ?? source.Reference),
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapRedeemCode(raw: unknown): RedeemCode {
  const source = record(raw);
  const expiresAt = source.expires_at ?? source.expiresAt ?? source.ExpiresAt;
  const validDays = source.valid_days ?? source.validDays ?? source.ValidDays;
  return {
    code: text(source.code ?? source.Code),
    amount: number(source.amount ?? source.Amount),
    maxUses: number(source.max_uses ?? source.maxUses ?? source.MaxUses, 1),
    usedCount: number(source.used_count ?? source.usedCount ?? source.UsedCount),
    expiresAt: typeof expiresAt === 'string' && expiresAt ? expiresAt : null,
    validDays: typeof validDays === 'number' && Number.isFinite(validDays) ? validDays : null,
    disabled: Boolean(source.disabled ?? source.Disabled),
    note: text(source.note ?? source.Note),
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapRun(raw: unknown): AIRun {
  const source = record(raw);
  const status = text(source.status ?? source.Status, 'failed');
  return {
    id: text(source.id ?? source.ID),
    userNickname: text(source.user_nickname ?? source.userNickname, text(source.user_id ?? source.userId, '用户')),
    gameTitle: text(source.game_title ?? source.gameTitle, text(source.game_id ?? source.gameId, '游戏')),
    channelName: text(source.channel_name ?? source.channelName) || undefined,
    provider: text(source.provider ?? source.Provider, 'unknown'),
    model: text(source.model ?? source.Model, 'unknown'),
    inputTokens: number(source.input_tokens ?? source.inputTokens),
    outputTokens: number(source.output_tokens ?? source.outputTokens),
    charged: number(source.charged_amount ?? source.chargedAmount ?? source.charged),
    status: status === 'completed' ? 'completed' : status === 'running' ? 'streaming' : 'failed',
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapLLMChannel(raw: unknown): LLMChannel {
  const source = record(raw);
  const health = text(source.health_status ?? source.healthStatus, 'unknown');
  return {
    id: text(source.id ?? source.ID),
    name: text(source.name ?? source.channel_name ?? source.channelName, '未命名渠道'),
    provider: text(source.provider, 'openai-compatible'),
    baseUrl: text(source.base_url ?? source.baseUrl),
    completionsPath: text(source.completions_path ?? source.completionsPath, '/chat/completions'),
    defaultModel: text(source.default_model ?? source.defaultModel),
    fallbackChannelId: source.fallback_channel_id === null || source.fallback_channel_id === undefined
      ? (typeof source.fallbackChannelId === 'string' ? source.fallbackChannelId : null)
      : text(source.fallback_channel_id) || null,
    fallbackChannelName: source.fallback_channel_name === null || source.fallback_channel_name === undefined
      ? (typeof source.fallbackChannelName === 'string' ? source.fallbackChannelName : null)
      : text(source.fallback_channel_name) || null,
    fallbackModel: source.fallback_model === null || source.fallback_model === undefined
      ? (typeof source.fallbackModel === 'string' ? source.fallbackModel : null)
      : text(source.fallback_model) || null,
    apiKeyConfigured: Boolean(source.api_key_configured ?? source.apiKeyConfigured),
    enabled: Boolean(source.enabled),
    isDefault: Boolean(source.is_default ?? source.isDefault),
    timeoutSeconds: number(source.timeout_seconds ?? source.timeoutSeconds, 180),
    healthStatus: health === 'healthy' || health === 'unhealthy' ? health : 'unknown',
    lastCheckedAt: typeof (source.last_checked_at ?? source.lastCheckedAt) === 'string' ? String(source.last_checked_at ?? source.lastCheckedAt) : null,
    lastError: typeof (source.last_error ?? source.lastError) === 'string' ? String(source.last_error ?? source.lastError) : null,
    createdAt: text(source.created_at ?? source.createdAt, new Date(0).toISOString()),
    updatedAt: text(source.updated_at ?? source.updatedAt, new Date(0).toISOString()),
  };
}

function mapS3Settings(raw: unknown): S3Settings {
  const source = record(raw);
  const provider = text(source.provider);
  return {
    configured: Boolean(source.configured),
    enabled: Boolean(source.enabled),
    provider: provider === 'aws_s3' || provider === 's3_compatible' ? provider : '',
    endpoint: text(source.endpoint),
    region: text(source.region),
    bucket: text(source.bucket),
    publicBaseUrl: text(source.public_base_url ?? source.publicBaseUrl),
    accessKeyId: text(source.access_key_id ?? source.accessKeyId),
    secretConfigured: Boolean(source.secret_configured ?? source.secretConfigured),
    forcePathStyle: Boolean(source.force_path_style ?? source.forcePathStyle),
    source: source.source === 'database' || source.source === 'environment' ? source.source : 'none',
    updatedAt: typeof (source.updated_at ?? source.updatedAt) === 'string' ? String(source.updated_at ?? source.updatedAt) : null,
  };
}

function mapAIPriceRule(raw: unknown): AIPriceRule {
  const source = record(raw);
  const mode = text(source.deduction_mode ?? source.deductionMode, 'token_rate');
  const tiers = Array.isArray(source.tiers) ? source.tiers.map((value) => {
    const tier = record(value);
    return {
      maxInputTokens: number(tier.max_input_tokens ?? tier.maxInputTokens),
      inputPriceMicrosPerMillion: number(tier.input_price_micros_per_million ?? tier.inputPriceMicrosPerMillion),
      outputPriceMicrosPerMillion: number(tier.output_price_micros_per_million ?? tier.outputPriceMicrosPerMillion),
      inputInspirationPerMillion: number(tier.input_inspiration_per_million ?? tier.inputInspirationPerMillion),
      outputInspirationPerMillion: number(tier.output_inspiration_per_million ?? tier.outputInspirationPerMillion),
    };
  }) : [];
  return {
    id: text(source.id ?? source.ID),
    provider: text(source.provider),
    model: text(source.model),
    deductionMode: mode === 'fixed_per_request' ? 'fixed_per_request' : 'token_rate',
    inspirationPerYuan: number(source.inspiration_per_yuan ?? source.inspirationPerYuan, 1000),
    fixedInspiration: number(source.fixed_inspiration ?? source.fixedInspiration),
    tiers,
    version: text(source.version, 'unknown'),
    active: Boolean(source.active),
    createdAt: text(source.created_at ?? source.createdAt, new Date(0).toISOString()),
  };
}

function aiPriceRulePayload(input: AIPriceRuleWriteInput): Record<string, unknown> {
  return {
    provider: input.provider.trim(),
    model: input.model.trim(),
    deduction_mode: input.deductionMode,
    inspiration_per_yuan: input.inspirationPerYuan,
    fixed_inspiration: input.fixedInspiration,
    tiers: input.tiers.map((tier) => ({
      max_input_tokens: tier.maxInputTokens,
      input_price_micros_per_million: tier.inputPriceMicrosPerMillion,
      output_price_micros_per_million: tier.outputPriceMicrosPerMillion,
    })),
    active: input.active,
  };
}

function mapInspirationPackage(raw: unknown): InspirationPackageReference {
  const source = record(raw);
  return {
    id: text(source.id),
    name: text(source.name, '灵感套餐'),
    amountFen: number(source.amount_fen ?? source.amountFen),
    inspiration: number(source.inspiration),
    badge: text(source.badge),
  };
}

function s3Payload(input: S3SettingsInput): Record<string, unknown> {
  return {
    enabled: true,
    provider: input.provider,
    endpoint: input.endpoint.trim(),
    region: input.region.trim(),
    bucket: input.bucket.trim(),
    public_base_url: input.publicBaseUrl.trim(),
    access_key_id: input.accessKeyId.trim(),
    ...(input.secretAccessKey.trim() ? { secret_access_key: input.secretAccessKey.trim() } : {}),
    force_path_style: input.forcePathStyle,
  };
}

function channelPayload(input: LLMChannelWriteInput): Record<string, unknown> {
  return {
    name: input.name,
    provider: input.provider,
    base_url: input.baseUrl,
    ...(input.apiKey?.trim() ? { api_key: input.apiKey.trim() } : {}),
    completions_path: input.completionsPath,
    default_model: input.defaultModel,
    fallback_channel_id: input.fallbackChannelId,
    fallback_model: input.fallbackModel,
    enabled: true,
    is_default: input.isDefault,
    timeout_seconds: input.timeoutSeconds,
  };
}

function mapAudit(raw: unknown): AuditLog {
  const source = record(raw);
  const detail = source.details ?? source.detail;
  const detailText = typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '';
  return {
    id: text(source.id ?? source.ID),
    actor: text(source.actor_id ?? source.actorId ?? source.ActorID, 'system'),
    action: text(source.action ?? source.Action),
    target: `${text(source.resource_type ?? source.resourceType)} / ${text(source.resource_id ?? source.resourceId)}`.trim(),
    severity: 'info',
    detail: detailText,
    requestId: text(source.request_id ?? source.requestId ?? source.RequestID),
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapGameRecord(raw: unknown): GameSummary {
  const source = record(raw);
  const statusValue = text(source.status ?? source.Status, 'draft');
  return {
    id: text(source.id ?? source.ID),
    slug: text(source.slug ?? source.Slug),
    title: text(source.title ?? source.Title, '未命名游戏'),
    category: text(source.category ?? source.Category, '互动叙事'),
    tags: Array.isArray(source.tags) ? source.tags.filter((value): value is string => typeof value === 'string') : [],
    description: text(source.description ?? source.Description),
    status: statusValue === 'published' ? 'published' : statusValue === 'unpublished' ? 'paused' : 'draft',
    version: text(source.version ?? source.Version, 'unversioned'),
    runtimeVersion: text(source.runtime_min_version ?? source.runtimeMinVersion ?? source.RuntimeMinVersion, '未声明'),
    saveSchemaVersion: number(source.save_schema_version ?? source.saveSchemaVersion),
    players: source.players === undefined ? null : number(source.players),
    completionRate: (source.completion_rate ?? source.completionRate) === undefined ? null : number(source.completion_rate ?? source.completionRate),
    lastPublishedAt: typeof source.published_at === 'string' ? source.published_at : null,
    updatedAt: text(source.updated_at ?? source.updatedAt ?? source.UpdatedAt, new Date(0).toISOString()),
    cover: text(source.cover_url ?? source.coverUrl ?? source.CoverURL),
    coverVariants: mapCoverVariants(source.cover_variants ?? source.coverVariants),
    capabilities: Array.isArray(source.capabilities) ? source.capabilities.filter((value): value is string => typeof value === 'string') : [],
  };
}

function mapGameAIPrompt(raw: unknown): GameAIPromptConfig {
  const source = record(raw);
  return {
    gameId: text(source.game_id ?? source.gameId),
    slug: text(source.slug),
    promptVersion: text(source.prompt_version ?? source.promptVersion),
    world: text(source.world),
    protagonist: text(source.protagonist),
    opening: text(source.opening),
    rules: Array.isArray(source.rules) ? source.rules.filter((value): value is string => typeof value === 'string') : [],
    systemTemplate: text(source.system_template ?? source.systemTemplate),
    newUserTemplate: text(source.new_user_template ?? source.newUserTemplate),
    continueUserTemplate: text(source.continue_user_template ?? source.continueUserTemplate),
    memorySystemTemplate: text(source.memory_system_template ?? source.memorySystemTemplate),
    memoryUserTemplate: text(source.memory_user_template ?? source.memoryUserTemplate),
    maxContextTurns: number(source.max_context_turns ?? source.maxContextTurns),
    maxContextCharacters: number(source.max_context_characters ?? source.maxContextCharacters),
    memoryCompressionBatchTurns: number(source.memory_compression_batch_turns ?? source.memoryCompressionBatchTurns),
    memoryMaxOutputTokens: number(source.memory_max_output_tokens ?? source.memoryMaxOutputTokens),
    maxOutputTokens: number(source.max_output_tokens ?? source.maxOutputTokens),
    updatedAt: text(source.updated_at ?? source.updatedAt, new Date(0).toISOString()),
  };
}

function mapGameAIPromptEntry(raw: unknown): GameAIPrompt {
  const source = record(raw);
  const kind = text(source.kind);
  return {
    id: text(source.id),
    gameId: text(source.game_id ?? source.gameId),
    name: text(source.name),
    kind: kind === 'base' || kind === 'compression' ? kind : 'custom',
    content: text(source.content),
    maxInputTokens: number(source.max_input_tokens ?? source.maxInputTokens),
    maxOutputTokens: number(source.max_output_tokens ?? source.maxOutputTokens),
    createdAt: text(source.created_at ?? source.createdAt, new Date(0).toISOString()),
    updatedAt: text(source.updated_at ?? source.updatedAt, new Date(0).toISOString()),
  };
}

function gameAIPromptEntryPayload(input: GameAIPromptEntryWriteInput): Record<string, unknown> {
  return { name: input.name.trim(), content: input.content.trim(), max_input_tokens: input.maxInputTokens, max_output_tokens: input.maxOutputTokens };
}

function gameAIPromptPayload(input: GameAIPromptWriteInput): Record<string, unknown> {
  return {
    prompt_version: input.promptVersion.trim(),
    world: input.world.trim(),
    protagonist: input.protagonist.trim(),
    opening: input.opening.trim(),
    rules: input.rules.map((rule) => rule.trim()).filter(Boolean),
    system_template: input.systemTemplate.trim(),
    new_user_template: input.newUserTemplate.trim(),
    continue_user_template: input.continueUserTemplate.trim(),
    memory_system_template: input.memorySystemTemplate.trim(),
    memory_user_template: input.memoryUserTemplate.trim(),
    max_context_turns: input.maxContextTurns,
    max_context_characters: input.maxContextCharacters,
    memory_compression_batch_turns: input.memoryCompressionBatchTurns,
    memory_max_output_tokens: input.memoryMaxOutputTokens,
    max_output_tokens: input.maxOutputTokens,
  };
}

function mapAsset(raw: unknown): GameAsset {
  const source = record(raw);
  return {
    id: text(source.id ?? source.ID),
    gameId: text(source.game_id ?? source.gameId ?? source.GameID),
    key: text(source.key ?? source.Key),
    url: text(source.url ?? source.URL),
    contentType: text(source.content_type ?? source.contentType ?? source.ContentType),
    size: number(source.size ?? source.Size),
    checksum: text(source.checksum ?? source.Checksum),
    createdAt: text(source.created_at ?? source.createdAt ?? source.CreatedAt, new Date(0).toISOString()),
  };
}

function mapPresign(raw: unknown): PresignAssetUpload {
  const source = record(raw);
  const headers = record(source.headers);
  return {
    key: text(source.key),
    uploadUrl: text(source.upload_url ?? source.uploadUrl),
    expiresAt: text(source.expires_at ?? source.expiresAt, new Date(0).toISOString()),
    headers: Object.fromEntries(Object.entries(headers).filter((entry): entry is [string, string] => typeof entry[1] === 'string')),
    checksum: text(source.checksum),
  };
}

function gamePayload(input: GameWriteInput): Record<string, unknown> {
  return {
    slug: input.slug.trim(),
    title: input.title.trim(),
    description: input.description.trim(),
    category: input.category.trim(),
    ...(input.tags !== undefined ? { tags: input.tags } : {}),
    runtime_min_version: input.runtimeVersion.trim(),
  };
}

function gameUpdatePayload(input: GameUpdateInput): Record<string, unknown> {
  const coverAssets = input.coverAssets
    ? Object.fromEntries(Object.entries(input.coverAssets).map(([slot, assetId]) => [slot, assetId]))
    : undefined;
  return {
    title: input.title.trim(),
    description: input.description.trim(),
    category: input.category.trim(),
    ...(input.tags !== undefined ? { tags: input.tags } : {}),
    runtime_min_version: input.runtimeVersion.trim(),
    ...(coverAssets ? { cover_assets: coverAssets } : {}),
  };
}

function emptyGame(id: string, status: GameStatus): GameSummary {
  return { id, slug: '', title: '', category: '', description: '', status, version: 'unknown', runtimeVersion: '未声明', saveSchemaVersion: 0, players: null, completionRate: null, lastPublishedAt: null, updatedAt: new Date().toISOString(), cover: '', coverVariants: {}, capabilities: [] };
}

function localDateKey(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function weeklyRunCounts(runs: AIRun[]): number[] {
  const today = new Date();
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (6 - index));
    const key = localDateKey(date);
    return runs.filter((run) => localDateKey(run.createdAt) === key).length;
  });
}

async function listAdminGames(options: { query?: string; status?: GameStatus | 'all' } = {}): Promise<ApiListResult<GameSummary>> {
  let games: GameSummary[];
  try {
    const payload = await request<{ items?: unknown[] }>('/admin/games?limit=200');
    games = (payload.items ?? []).map(mapGameRecord);
  } catch (error) {
    if (!(error instanceof ApiClientError) || error.status !== 404) throw error;
    games = (await sharedClient.listGames()).map(mapSharedGame);
  }
  const normalized = options.query?.trim().toLowerCase();
  const items = games.filter((game) => {
    const matchesQuery = !normalized || `${game.title} ${game.slug} ${game.category}`.toLowerCase().includes(normalized);
    const matchesStatus = !options.status || options.status === 'all' || game.status === options.status;
    return matchesQuery && matchesStatus;
  });
  return { items, total: items.length };
}

export const httpAdminApi: AdminApi = {
  async getDashboard() {
    const [games, users, runs, audit] = await Promise.all([
      listAdminGames(),
      this.listUsers(),
      this.listAIRuns(),
      this.listAuditLogs(),
    ]);
    const today = localDateKey(new Date());
    return {
      activeGames: games.items.filter((game) => game.status === 'published').length,
      onlinePlayers: users.total,
      inspirationConsumed: runs.items.filter((run) => localDateKey(run.createdAt) === today).reduce((sum, run) => sum + run.charged, 0),
      aiRunsToday: runs.items.filter((run) => localDateKey(run.createdAt) === today).length,
      publishSuccessRate: games.total > 0 ? Math.round((games.items.filter((game) => game.status === 'published').length / games.total) * 100) : 0,
      weeklyRuns: weeklyRunCounts(runs.items),
      recentActivity: audit.items.slice(0, 8),
    } satisfies DashboardMetrics;
  },
  async listAnnouncements(options: AnnouncementListOptions = {}) {
    const pageSize = Math.max(1, Math.min(options.pageSize ?? 10, 100));
    const page = Math.max(1, options.page ?? 1);
    const params = new URLSearchParams({ limit: String(pageSize), page: String(page) });
    if (options.query?.trim()) params.set('query', options.query.trim());
    if (options.status && options.status !== 'all') params.set('status', options.status);
    if (options.platform && options.platform !== 'all') params.set('platform', options.platform);
    const payload = await request<{ items?: unknown[]; total?: number }>(`/admin/announcements?${params.toString()}`);
    const items = (payload.items ?? []).map(mapAnnouncement);
    return { items, total: number(payload.total, items.length) };
  },
  async createAnnouncement(input) {
    return mapAnnouncement(await request<unknown>('/admin/announcements', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(announcementPayload(input)) }));
  },
  async updateAnnouncement(id, input) {
    return mapAnnouncement(await request<unknown>(`/admin/announcements/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(announcementPayload(input)) }));
  },
  async publishAnnouncement(id) {
    return mapAnnouncement(await request<unknown>(`/admin/announcements/${encodeURIComponent(id)}/publish`, { method: 'POST' }));
  },
  async archiveAnnouncement(id) {
    return mapAnnouncement(await request<unknown>(`/admin/announcements/${encodeURIComponent(id)}/archive`, { method: 'POST' }));
  },
  listGames: listAdminGames,
  async getGame(id) {
    try {
      return mapGameRecord(await request<unknown>(`/admin/games/${encodeURIComponent(id)}`));
    } catch (error) {
      if (!(error instanceof ApiClientError) || error.status !== 404) throw error;
      const result = await listAdminGames();
      return result.items.find((game) => game.id === id) ?? null;
    }
  },
  async getGameAIPrompt(id) {
    try {
      return mapGameAIPrompt(await request<unknown>(`/admin/games/${encodeURIComponent(id)}/ai-prompt`));
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404) return null;
      throw error;
    }
  },
  async updateGameAIPrompt(id, input) {
    return mapGameAIPrompt(await request<unknown>(`/admin/games/${encodeURIComponent(id)}/ai-prompt`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(gameAIPromptPayload(input)),
    }));
  },
  async listGameAIPrompts(id) {
    const payload = record(await request<unknown>(`/admin/games/${encodeURIComponent(id)}/ai-prompts`));
    return { items: Array.isArray(payload.items) ? payload.items.map(mapGameAIPromptEntry) : [], total: number(payload.total ?? (Array.isArray(payload.items) ? payload.items.length : 0)) };
  },
  async createGameAIPrompt(id, input) {
    return mapGameAIPromptEntry(await request<unknown>(`/admin/games/${encodeURIComponent(id)}/ai-prompts`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(gameAIPromptEntryPayload(input)) }));
  },
  async updateGameAIPromptEntry(gameId, promptId, input) {
    return mapGameAIPromptEntry(await request<unknown>(`/admin/games/${encodeURIComponent(gameId)}/ai-prompts/${encodeURIComponent(promptId)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(gameAIPromptEntryPayload(input)) }));
  },
  async deleteGameAIPrompt(gameId, promptId) {
    await request<unknown>(`/admin/games/${encodeURIComponent(gameId)}/ai-prompts/${encodeURIComponent(promptId)}`, { method: 'DELETE' });
  },
  async createGame(input) {
    return mapGameRecord(await request<unknown>('/admin/games', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(gamePayload(input)) }));
  },
  async updateGame(id, input) {
    return mapGameRecord(await request<unknown>(`/admin/games/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(gameUpdatePayload(input)) }));
  },
  async uploadAsset(gameId, file) {
    const body = new FormData();
    body.set('game_id', gameId);
    body.set('file', file, file.name);
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    body.set('checksum', Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(''));
    return mapAsset(await request<unknown>('/admin/assets/upload', { method: 'POST', body }));
  },
  async presignAssetUpload(input) {
    return mapPresign(await request<unknown>('/admin/assets/presign-upload', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ game_id: input.gameId, filename: input.filename, content_type: input.contentType, size: input.size, checksum: input.checksum }) }));
  },
  async confirmAssetUpload(input) {
    return mapAsset(await request<unknown>('/admin/assets/confirm-upload', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ game_id: input.gameId, key: input.key, content_type: input.contentType, size: input.size, checksum: input.checksum }) }));
  },
  async listReleases(gameId) {
    const payload = await request<{ items?: unknown[] }>(`/admin/games/${encodeURIComponent(gameId)}/releases`);
    const items = (payload.items ?? []).map(mapRelease);
    return { items, total: items.length };
  },
  async publishGame(gameId) {
    await request<void>(`/admin/games/${encodeURIComponent(gameId)}/publish`, { method: 'POST' });
    return (await this.getGame(gameId)) ?? emptyGame(gameId, 'published');
  },
  async pauseGame(gameId) {
    await request<void>(`/admin/games/${encodeURIComponent(gameId)}/unpublish`, { method: 'POST' });
    return (await this.getGame(gameId)) ?? emptyGame(gameId, 'paused');
  },
  async publishRelease(gameId, releaseId) {
    await request<void>(`/admin/games/${encodeURIComponent(gameId)}/releases/${encodeURIComponent(releaseId)}/publish`, { method: 'POST' });
    const releases = await this.listReleases(gameId);
    return releases.items.find((release) => release.id === releaseId) ?? { id: releaseId, gameId, version: 'unknown', siteBuildId: 'unknown', status: 'published', changelog: '', checksum: '', createdAt: new Date().toISOString(), createdBy: 'system' };
  },
  async rollbackRelease(gameId, releaseId) {
    await request<void>(`/admin/games/${encodeURIComponent(gameId)}/releases/${encodeURIComponent(releaseId)}/rollback`, { method: 'POST' });
    const releases = await this.listReleases(gameId);
    return releases.items.find((release) => release.id === releaseId) ?? { id: releaseId, gameId, version: 'unknown', siteBuildId: 'unknown', status: 'rolled_back', changelog: '', checksum: '', createdAt: new Date().toISOString(), createdBy: 'system' };
  },
  async listUsers(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/users?limit=200');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapUser).filter((user) => !normalized || `${user.id} ${user.nickname} ${user.phone}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async listLedger(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/inspiration-ledger?limit=200');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapLedger).filter((entry) => !normalized || `${entry.userId} ${entry.reason} ${entry.reference}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async adjustInspiration(input) {
    const reference = `admin:${createClientId()}`;
    await request<void>('/admin/inspiration-adjustments', {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ user_id: input.userId, amount: input.amount, reason: input.reason, reference, ...(input.expiresAt ? { expires_at: input.expiresAt } : {}) }),
    });
    return { id: reference, userId: input.userId, userNickname: '用户', kind: 'adjustment', amount: input.amount, balanceAfter: null, reason: input.reason, reference, createdAt: new Date().toISOString() };
  },
  async listRedeemCodes(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/redeem-codes?limit=200');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapRedeemCode).filter((code) => !normalized || `${code.code} ${code.note}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async createRedeemCode(input) {
    const payload = await request<unknown>('/admin/redeem-codes', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        ...(input.code?.trim() ? { code: input.code.trim() } : {}),
        amount: input.amount,
        max_uses: input.maxUses,
        ...(input.expiresAt ? { expires_at: input.expiresAt } : {}),
        ...(input.validDays ? { valid_days: input.validDays } : {}),
        note: input.note ?? '',
      }),
    });
    return mapRedeemCode(payload);
  },
  async listAIRuns(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/ai-runs?limit=200');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapRun).filter((run) => !normalized || `${run.userNickname} ${run.gameTitle} ${run.model}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async listAuditLogs(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/audit-logs?limit=200');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapAudit).filter((log) => !normalized || `${log.action} ${log.target} ${log.detail}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async listLLMChannels(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/llm-channels');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapLLMChannel).filter((channel) => !normalized || `${channel.name} ${channel.provider} ${channel.baseUrl} ${channel.defaultModel}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async createLLMChannel(input) {
    const payload = await request<unknown>('/admin/llm-channels', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(channelPayload(input)) });
    return mapLLMChannel(payload);
  },
  async updateLLMChannel(id, input) {
    const payload = await request<unknown>(`/admin/llm-channels/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(channelPayload(input)) });
    return mapLLMChannel(payload);
  },
  async setLLMChannelEnabled(id, enabled) {
    const payload = await request<unknown>(`/admin/llm-channels/${encodeURIComponent(id)}/enabled`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ enabled }) });
    return mapLLMChannel(payload);
  },
  async probeLLMChannel(id) {
    const payload = await request<unknown>(`/admin/llm-channels/${encodeURIComponent(id)}/probe`, { method: 'POST' });
    return mapLLMChannel(payload);
  },
  async listAIPriceRules(query) {
    const payload = await request<{ items?: unknown[] }>('/admin/ai-price-rules');
    const normalized = query?.trim().toLowerCase();
    const items = (payload.items ?? []).map(mapAIPriceRule).filter((rule) => !normalized || `${rule.provider} ${rule.model} ${rule.deductionMode}`.toLowerCase().includes(normalized));
    return { items, total: items.length };
  },
  async createAIPriceRule(input) {
    return mapAIPriceRule(await request<unknown>('/admin/ai-price-rules', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(aiPriceRulePayload(input)) }));
  },
  async updateAIPriceRule(id, input) {
    return mapAIPriceRule(await request<unknown>(`/admin/ai-price-rules/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(aiPriceRulePayload(input)) }));
  },
  async setAIPriceRuleActive(id, active) {
    return mapAIPriceRule(await request<unknown>(`/admin/ai-price-rules/${encodeURIComponent(id)}/active`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ active }) }));
  },
  async listInspirationPackages() {
    const payload = await request<{ items?: unknown[] }>('/inspiration/packages');
    return (payload.items ?? []).map(mapInspirationPackage);
  },
  async getS3Settings() {
    return mapS3Settings(await request<unknown>('/admin/system/s3'));
  },
  async saveS3Settings(input) {
    return mapS3Settings(await request<unknown>('/admin/system/s3', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(s3Payload(input)) }));
  },
  async testS3Connection(input) {
    const payload = record(await request<unknown>('/admin/system/s3/test', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(s3Payload(input)) }));
    return { latencyMs: number(payload.latency_ms ?? payload.latencyMs), testedAt: text(payload.tested_at ?? payload.testedAt, new Date().toISOString()) } satisfies S3ConnectionResult;
  },
};
